import { Briefcase, FileEdit, Globe, Mail, MessageSquare, Palette, Phone, StickyNote, User } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui";
import { cn } from "@/lib/utils";
import { VideoProject } from "@/services/bedrock";
import { printDateToChat } from "@/utils";
import { StylePreferencesOptions } from "@/utils/projectHelpers";

import { hasReadableNotes, SpotlightRegion } from "./qaStudio";
import { HudCard, LinkifiedText, PlayerInfoRow } from "./studioChrome";

export function QaContextRail({
  isSpotlighted,
  hasPlayerInfo,
  playerName,
  playerAliases,
  companyName,
  regionsAndBranches,
  player,
  caption,
  captionBlockedWords,
  preferencesInfo,
  formatHexColor,
  playerNotes,
  coachNotes,
  videoProjectMessages,
}: {
  isSpotlighted: (region: SpotlightRegion) => boolean;
  hasPlayerInfo: boolean;
  playerName?: string | null;
  playerAliases: string[];
  companyName?: string | null;
  regionsAndBranches: string[];
  player?: VideoProject["player"];
  caption: string;
  captionBlockedWords: string[];
  preferencesInfo: StylePreferencesOptions[];
  formatHexColor: (color: string) => string | null;
  playerNotes?: VideoProject.ClientNote;
  coachNotes?: VideoProject.ClientNote;
  videoProjectMessages?: VideoProject.Message[];
}) {
  return (
    <div className="-mx-1 min-h-0 overflow-y-auto px-1">
      <div className="flex flex-col gap-6 pt-1 pb-4">
        <HudCard active={isSpotlighted("client")} title="Client Info" icon={User} iconClass="text-emerald-600">
          {hasPlayerInfo ? (
            <div className="flex flex-col gap-1.5">
              {playerName || playerAliases.length ? (
                <PlayerInfoRow icon={User} title="Name">
                  <div className="flex flex-col gap-0.5">
                    {playerName ? <span>{playerName}</span> : null}
                    {playerAliases.length ? (
                      <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                        {playerAliases.join(", ")}
                      </span>
                    ) : null}
                  </div>
                </PlayerInfoRow>
              ) : null}
              {companyName || regionsAndBranches.length ? (
                <PlayerInfoRow icon={Briefcase} title="Company">
                  <div className="flex flex-col gap-1">
                    {companyName ? <span>{companyName}</span> : null}
                    {regionsAndBranches.length ? (
                      <div className="flex flex-row flex-wrap items-center gap-1">
                        {regionsAndBranches.map(segment => (
                          <Badge
                            key={segment}
                            variant="outline"
                            className="border-slate-100 text-[10px] font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300"
                          >
                            {segment}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </PlayerInfoRow>
              ) : null}
              {player?.email ? (
                <PlayerInfoRow icon={Mail} title="Email">
                  <span className="wrap-break-word">{player.email}</span>
                </PlayerInfoRow>
              ) : null}
              {player?.phone ? (
                <PlayerInfoRow icon={Phone} title="Phone">
                  {player.phone}
                </PlayerInfoRow>
              ) : null}
              {player?.webAddress ? (
                <PlayerInfoRow icon={Globe} title="Website">
                  <span className="wrap-break-word">{player.webAddress}</span>
                </PlayerInfoRow>
              ) : null}
            </div>
          ) : (
            <p className="py-1 text-center text-xs font-medium text-slate-400 dark:text-slate-500">
              No Client details loaded
            </p>
          )}
        </HudCard>
        <HudCard active={isSpotlighted("post")} title="Post Info" icon={FileEdit} iconClass="text-indigo-600">
          <div className="flex flex-col gap-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 select-none dark:text-slate-500">
              Caption
            </p>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-slate-200">
              {caption || "No caption"}
            </p>
          </div>
          {captionBlockedWords.length ? (
            <p className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-300">
              Blocked words: {captionBlockedWords.join(", ")}
            </p>
          ) : null}
        </HudCard>
        <HudCard active={isSpotlighted("spec")} title="Style Preferences" icon={Palette} iconClass="text-indigo-600">
          <div className="flex flex-wrap gap-1.5">
            {preferencesInfo.length ? (
              preferencesInfo.map(pref => (
                <span
                  key={pref.key}
                  className={cn(
                    "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                    pref.key.toLowerCase().startsWith("no ")
                      ? "border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                      : "border-indigo-100 bg-indigo-50/50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-500/10 dark:text-indigo-300"
                  )}
                >
                  {pref.key}
                  {pref.colors?.length ? (
                    <span className="ml-1 inline-flex gap-0.5">
                      {pref.colors.map((color, index) => (
                        <span
                          key={`${color}-${index}`}
                          className="inline-block size-2.5 rounded-sm border border-slate-200 dark:border-slate-600"
                          style={{ backgroundColor: formatHexColor(color) || color }}
                        />
                      ))}
                    </span>
                  ) : null}
                </span>
              ))
            ) : (
              <span className="text-slate-400 dark:text-slate-500">No stored preferences</span>
            )}
          </div>
        </HudCard>
        <HudCard active={isSpotlighted("notes")} title="Client Notes" icon={StickyNote} iconClass="text-emerald-600">
          {hasReadableNotes(playerNotes, coachNotes) ? (
            <div className="flex flex-col gap-4 text-sm text-slate-700 dark:text-slate-200">
              {coachNotes?.notes?.trim() ? (
                <div className="flex flex-col gap-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 select-none dark:text-slate-500">
                    Coach Notes (all players)
                  </p>
                  <LinkifiedText>{coachNotes.notes}</LinkifiedText>
                </div>
              ) : null}
              {coachNotes?.notes?.trim() && playerNotes?.notes?.trim() ? (
                <div className="border-t border-slate-200/60 dark:border-slate-700/60" />
              ) : null}
              {playerNotes?.notes?.trim() ? (
                <div className="flex flex-col gap-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 select-none dark:text-slate-500">
                    Player Notes
                  </p>
                  <LinkifiedText>{playerNotes.notes}</LinkifiedText>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="py-1 text-center text-xs font-medium text-slate-400 dark:text-slate-500">
              No client notes added yet.
            </p>
          )}
        </HudCard>
        <HudCard
          active={isSpotlighted("notes")}
          title="Messages"
          icon={MessageSquare}
          iconClass="text-sky-600"
          action={
            videoProjectMessages?.length ? (
              <Badge
                variant="outline"
                className="border-sky-100 bg-sky-50 font-bold text-sky-700 dark:border-sky-800 dark:bg-sky-500/15 dark:text-sky-300"
              >
                {videoProjectMessages.length}
              </Badge>
            ) : null
          }
        >
          {videoProjectMessages?.length ? (
            <div className="space-y-2 text-slate-700 dark:text-slate-200">
              {videoProjectMessages.map(message => (
                <div key={message.id} className="space-y-0.5">
                  <p className="text-[10px] font-semibold text-slate-400 dark:text-slate-500">
                    {message.userName || message.userRole || "Message"}
                    {message.createdDate ? ` · ${printDateToChat(message.createdDate)}` : ""}
                  </p>
                  <LinkifiedText>{message.content || ""}</LinkifiedText>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 dark:text-slate-500">No messages</p>
          )}
        </HudCard>
      </div>
    </div>
  );
}
